import axios from "axios";

class TrieNode {
  public readonly children: Record<string, TrieNode>;
  public isEndOfWord: boolean;

  constructor() {
    this.children = {};
    this.isEndOfWord = false;
  }
}

class Trie {
  private readonly root: TrieNode;

  constructor(list: string[]) {
    this.root = new TrieNode();
    this.buildTrie(list);
  }

  private buildTrie(words: string[]): void {
    for (const word of words) {
      let node = this.root;
      const lowerWord = word.toLowerCase();

      for (const char of lowerWord) {
        if (!node.children[char]) {
          node.children[char] = new TrieNode();
        }
        node = node.children[char];
      }
      node.isEndOfWord = true;
    }
  }

  search(prefix: string) {
    prefix = prefix.toLowerCase();
    let node = this.root;
    for (const char of prefix) {
      if (!node.children[char]) return [];
      node = node.children[char];
    }
    return this.collectWords(node, prefix);
  }

  private collectWords(node: TrieNode, prefix: string): string[] {
    let results = [];
    if (node.isEndOfWord) results.push(prefix);
    for (const char in node.children) {
      results.push(...this.collectWords(node.children[char], prefix + char));
    }
    return results;
  }
}

class StateFinder {
  private readonly IndianStates = {
    AN: "Andaman and Nicobar Islands",
    AP: "Andhra Pradesh",
    AR: "Arunachal Pradesh",
    AS: "Assam",
    BR: "Bihar",
    CG: "Chandigarh",
    CH: "Chhattisgarh",
    DNHDD: "Dadra and Nagar Haveli and Daman and Diu",
    DL: "Delhi",
    GA: "Goa",
    GJ: "Gujarat",
    HR: "Haryana",
    HP: "Himachal Pradesh",
    JK: "Jammu and Kashmir",
    JH: "Jharkhand",
    KA: "Karnataka",
    KL: "Kerala",
    LA: "Ladakh",
    LD: "Lakshadweep",
    MP: "Madhya Pradesh",
    MH: "Maharashtra",
    MN: "Manipur",
    ML: "Meghalaya",
    MZ: "Mizoram",
    NL: "Nagaland",
    OR: "Odisha",
    PY: "Puducherry",
    PB: "Punjab",
    RJ: "Rajasthan",
    SK: "Sikkim",
    TN: "Tamil Nadu",
    TS: "Telangana",
    TR: "Tripura",
    UP: "Uttar Pradesh",
    UK: "Uttarakhand",
    WB: "West Bengal",
  };

  private readonly Countries = {
    AF: "Afghanistan",
    AX: "Åland Islands",
    AL: "Albania",
    DZ: "Algeria",
    AS: "American Samoa",
    AD: "Andorra",
    AO: "Angola",
    AI: "Anguilla",
    AQ: "Antarctica",
    AG: "Antigua and Barbuda",
    AR: "Argentina",
    AM: "Armenia",
    AW: "Aruba",
    AU: "Australia",
    AT: "Austria",
    AZ: "Azerbaijan",
    BS: "Bahamas",
    BH: "Bahrain",
    BD: "Bangladesh",
    BB: "Barbados",
    BY: "Belarus",
    BE: "Belgium",
    BZ: "Belize",
    BJ: "Benin",
    BM: "Bermuda",
    BT: "Bhutan",
    BO: "Bolivia",
    BA: "Bosnia and Herzegovina",
    BW: "Botswana",
    BV: "Bouvet Island",
    BR: "Brazil",
    IO: "British Indian Ocean Territory",
    BN: "Brunei Darussalam",
    BG: "Bulgaria",
    BF: "Burkina Faso",
    BI: "Burundi",
    KH: "Cambodia",
    CM: "Cameroon",
    CA: "Canada",
    CV: "Cape Verde",
    KY: "Cayman Islands",
    CF: "Central African Republic",
    TD: "Chad",
    CL: "Chile",
    CN: "China",
    CX: "Christmas Island",
    CC: "Cocos (Keeling) Islands",
    CO: "Colombia",
    KM: "Comoros",
    CG: "Congo",
    CD: "Congo, The Democratic Republic of the",
    CK: "Cook Islands",
    CR: "Costa Rica",
    CI: "Cote D'Ivoire",
    HR: "Croatia",
    CU: "Cuba",
    CY: "Cyprus",
    CZ: "Czech Republic",
    DK: "Denmark",
    DJ: "Djibouti",
    DM: "Dominica",
    DO: "Dominican Republic",
    EC: "Ecuador",
    EG: "Egypt",
    SV: "El Salvador",
    GQ: "Equatorial Guinea",
    ER: "Eritrea",
    EE: "Estonia",
    ET: "Ethiopia",
    FK: "Falkland Islands (Malvinas)",
    FO: "Faroe Islands",
    FJ: "Fiji",
    FI: "Finland",
    FR: "France",
    GF: "French Guiana",
    PF: "French Polynesia",
    TF: "French Southern Territories",
    GA: "Gabon",
    GM: "Gambia",
    GE: "Georgia",
    DE: "Germany",
    GH: "Ghana",
    GI: "Gibraltar",
    GR: "Greece",
    GL: "Greenland",
    GD: "Grenada",
    GP: "Guadeloupe",
    GU: "Guam",
    GT: "Guatemala",
    GG: "Guernsey",
    GN: "Guinea",
    GW: "Guinea-Bissau",
    GY: "Guyana",
    HT: "Haiti",
    HM: "Heard Island and Mcdonald Islands",
    VA: "Holy See (Vatican City State)",
    HN: "Honduras",
    HK: "Hong Kong",
    HU: "Hungary",
    IS: "Iceland",
    IN: "India",
    ID: "Indonesia",
    IR: "Iran, Islamic Republic Of",
    IQ: "Iraq",
    IE: "Ireland",
    IM: "Isle of Man",
    IL: "Israel",
    IT: "Italy",
    JM: "Jamaica",
    JP: "Japan",
    JE: "Jersey",
    JO: "Jordan",
    KZ: "Kazakhstan",
    KE: "Kenya",
    KI: "Kiribati",
    KP: "Korea, Democratic People'S Republic of",
    KR: "Korea, Republic of",
    KW: "Kuwait",
    KG: "Kyrgyzstan",
    LA: "Lao People'S Democratic Republic",
    LV: "Latvia",
    LB: "Lebanon",
    LS: "Lesotho",
    LR: "Liberia",
    LY: "Libyan Arab Jamahiriya",
    LI: "Liechtenstein",
    LT: "Lithuania",
    LU: "Luxembourg",
    MO: "Macao",
    MK: "Macedonia, The Former Yugoslav Republic of",
    MG: "Madagascar",
    MW: "Malawi",
    MY: "Malaysia",
    MV: "Maldives",
    ML: "Mali",
    MT: "Malta",
    MH: "Marshall Islands",
    MQ: "Martinique",
    MR: "Mauritania",
    MU: "Mauritius",
    YT: "Mayotte",
    MX: "Mexico",
    FM: "Micronesia, Federated States of",
    MD: "Moldova, Republic of",
    MC: "Monaco",
    MN: "Mongolia",
    MS: "Montserrat",
    MA: "Morocco",
    MZ: "Mozambique",
    MM: "Myanmar",
    NA: "Namibia",
    NR: "Nauru",
    NP: "Nepal",
    NL: "Netherlands",
    AN: "Netherlands Antilles",
    NC: "New Caledonia",
    NZ: "New Zealand",
    NI: "Nicaragua",
    NE: "Niger",
    NG: "Nigeria",
    NU: "Niue",
    NF: "Norfolk Island",
    MP: "Northern Mariana Islands",
    NO: "Norway",
    OM: "Oman",
    PK: "Pakistan",
    PW: "Palau",
    PS: "Palestinian Territory, Occupied",
    PA: "Panama",
    PG: "Papua New Guinea",
    PY: "Paraguay",
    PE: "Peru",
    PH: "Philippines",
    PN: "Pitcairn",
    PL: "Poland",
    PT: "Portugal",
    PR: "Puerto Rico",
    QA: "Qatar",
    RE: "Reunion",
    RO: "Romania",
    RU: "Russian Federation",
    RW: "Rwanda",
    SH: "Saint Helena",
    KN: "Saint Kitts and Nevis",
    LC: "Saint Lucia",
    PM: "Saint Pierre and Miquelon",
    VC: "Saint Vincent and the Grenadines",
    WS: "Samoa",
    SM: "San Marino",
    ST: "Sao Tome and Principe",
    SA: "Saudi Arabia",
    SN: "Senegal",
    CS: "Serbia and Montenegro",
    SC: "Seychelles",
    SL: "Sierra Leone",
    SG: "Singapore",
    SK: "Slovakia",
    SI: "Slovenia",
    SB: "Solomon Islands",
    SO: "Somalia",
    ZA: "South Africa",
    GS: "South Georgia and the South Sandwich Islands",
    ES: "Spain",
    LK: "Sri Lanka",
    SD: "Sudan",
    SR: "Suriname",
    SJ: "Svalbard and Jan Mayen",
    SZ: "Swaziland",
    SE: "Sweden",
    CH: "Switzerland",
    SY: "Syrian Arab Republic",
    TW: "Taiwan, Province of China",
    TJ: "Tajikistan",
    TZ: "Tanzania, United Republic of",
    TH: "Thailand",
    TL: "Timor-Leste",
    TG: "Togo",
    TK: "Tokelau",
    TO: "Tonga",
    TT: "Trinidad and Tobago",
    TN: "Tunisia",
    TR: "Turkey",
    TM: "Turkmenistan",
    TC: "Turks and Caicos Islands",
    TV: "Tuvalu",
    UG: "Uganda",
    UA: "Ukraine",
    AE: "United Arab Emirates",
    GB: "United Kingdom",
    US: "United States",
    UM: "United States Minor Outlying Islands",
    UY: "Uruguay",
    UZ: "Uzbekistan",
    VU: "Vanuatu",
    VE: "Venezuela",
    VN: "Viet Nam",
    VG: "Virgin Islands, British",
    VI: "Virgin Islands, U.S.",
    WF: "Wallis and Futuna",
    EH: "Western Sahara",
    YE: "Yemen",
    ZM: "Zambia",
    ZW: "Zimbabwe",
  };

  private indianStateTrie: Trie | null = null;
  private countriesTrie: Trie | null = null;

  private isInitalised = false;
  private currentPossibleState: string | null = null;
  private currentPossilbeCountry: string | null = null;

  private async init() {
    this.indianStateTrie = new Trie(Object.values(this.IndianStates));
    this.countriesTrie = new Trie(Object.values(this.Countries));
    const storedState = localStorage.getItem("tfu_state");
    const storedCountry = localStorage.getItem("tfu_country");

    if (storedState && storedCountry) {
      this.currentPossibleState = storedState;
      this.currentPossilbeCountry = storedCountry;
    } else {
      try {
        const response = await axios.get(
          `${process.env.NEXT_PUBLIC_BASE_API}ip`
        );

        const responseData: {
          status: string;
          country: string;
          countryCode: string;
          region: string;
          regionName: string;
          city: string;
          zip: string;
          lat: number;
          lon: number;
          timezone: string;
          isp: string;
          org: string;
          as: string;
          query: string;
        } = response.data;

        if (response) {
          this.currentPossibleState =
            //@ts-ignore
            this.IndianStates[responseData.region];
          this.currentPossilbeCountry =
            //@ts-ignore
            this.Countries[responseData.countryCode];

          if (this.currentPossibleState && this.currentPossilbeCountry) {
            localStorage.setItem("tfu_state", this.currentPossibleState);
            localStorage.setItem("tfu_country", this.currentPossilbeCountry);
          }
        }
      } catch (error) {
        console.error("Error initializing StateFinder:", error);
        this.currentPossibleState = this.IndianStates["PB"];
        this.currentPossilbeCountry = this.Countries["IN"];
      }
    }
    this.isInitalised = true;
  }

  constructor() {
    if (typeof localStorage !== "undefined") {
      this.init();
    }
  }

  listStates = () => {
    return Object.values(this.IndianStates);
  };

  listCountries = () => {
    return Object.values(this.Countries);
  };

  listCountryCodes = () => {
    return Object.keys(this.Countries);
  };

  findState = (key: string) => {
    if (key in this.IndianStates) {
      return this.IndianStates[key as keyof typeof this.IndianStates];
    }
    return null;
  };

  getStateByCode = (stateCode: string) => {
    if (stateCode in this.IndianStates) {
      return Object.keys(this.IndianStates).find(
        (key) =>
          this.IndianStates[key as keyof typeof this.IndianStates] === stateCode
      );
    }
    return null;
  };

  getStateSuggestions = (prefix: string) => {
    if (!this.indianStateTrie) return [];
    return this.indianStateTrie.search(prefix);
  };

  getCountrySuggestions = (prefix: string) => {
    if (!this.countriesTrie) return [];
    return this.countriesTrie.search(prefix);
  };

  getPossibleState = async () => {
    if (!this.isInitalised) {
      await this.init();
    }
    if (this.currentPossibleState) {
      return this.currentPossibleState;
    }
    return null;
  };

  getPossibleCountry = async () => {
    if (!this.isInitalised) {
      await this.init();
    }
    if (this.currentPossilbeCountry) {
      return this.currentPossilbeCountry;
    }
    return null;
  };

  setUserStateAndCountry = (state: string, country: string) => {
    this.currentPossibleState = state;
    this.currentPossilbeCountry = country;
    localStorage.setItem("tfu_state", state);
    localStorage.setItem("tfu_country", country);
  };
}

export default StateFinder;
